import React, { useState, useEffect, useRef } from 'react';
import './ChatInbox.css';
import ConfirmDialog from '../common/ConfirmDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ChatInbox = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, conversationId: null });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  useEffect(() => {
    loadConversations();
    
    // Poll for updates every 3 seconds
    pollingInterval.current = setInterval(() => {
      loadConversations();
      if (selectedConversation) {
        loadMessages(selectedConversation.id);
      }
    }, 3000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !selectedConversation || isLoading) return;

    const messageContent = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/staff/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          content: messageContent
        })
      });

      const data = await response.json();
      setMessages([...messages, data]);
      loadConversations(); // Refresh conversation list
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeConversation = async () => {
    if (!selectedConversation) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/chat/conversations/${selectedConversation.id}/close`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setSelectedConversation(null);
      setMessages([]);
      loadConversations();
    } catch (error) {
      console.error('Error closing conversation:', error);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e?.stopPropagation(); // Prevent selecting the conversation when clicking delete
    
    // Open confirmation dialog
    setConfirmDialog({ isOpen: true, conversationId });
  };

  const handleConfirmDelete = async () => {
    if (confirmDialog.conversationId === 'bulk') {
      return handleConfirmBulkDelete();
    }

    const conversationId = confirmDialog.conversationId;
    setConfirmDialog({ isOpen: false, conversationId: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation(null);
          setMessages([]);
        }
        loadConversations();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ isOpen: false, conversationId: null });
    if (confirmDialog.conversationId === 'bulk') return; // keep select mode
  };

  const toggleSelectId = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredConversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredConversations.map(c => c.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmDialog({ isOpen: true, conversationId: 'bulk' });
  };

  const handleConfirmBulkDelete = async () => {
    setConfirmDialog({ isOpen: false, conversationId: null });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/conversations/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (response.ok) {
        if (selectedIds.has(selectedConversation?.id)) {
          setSelectedConversation(null);
          setMessages([]);
        }
        setSelectedIds(new Set());
        setSelectMode(false);
        loadConversations();
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'AGENT': { label: 'AI', color: '#3498db' },
      'WAITING': { label: 'Waiting', color: '#f39c12' },
      'ASSIGNED': { label: 'Active', color: '#27ae60' },
      'CLOSED': { label: 'Closed', color: '#95a5a6' }
    };
    const badge = badges[status] || badges['AGENT'];
    return <span className="status-badge" style={{ background: badge.color }}>{badge.label}</span>;
  };

  const filteredConversations = conversations.filter(conv => {
    if (filter === 'ALL') return true;
    return conv.status === filter;
  });

  return (
    <div className="chat-inbox">
      <div className="inbox-sidebar">
        <div className="inbox-header">
          <div className="inbox-header-top">
            <h2>Chat Inbox</h2>
            <button
              className={`btn-select-mode ${selectMode ? 'active' : ''}`}
              onClick={() => {
                setSelectMode(!selectMode);
                if (selectMode) setSelectedIds(new Set());
              }}
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          </div>
          {selectMode && (
            <div className="bulk-actions-bar">
              <label className="bulk-select-all">
                <input
                  type="checkbox"
                  checked={filteredConversations.length > 0 && selectedIds.size === filteredConversations.length}
                  onChange={toggleSelectAll}
                />
                Select All
              </label>
              <button
                className="btn-bulk-delete"
                disabled={selectedIds.size === 0}
                onClick={handleBulkDelete}
              >
                Delete ({selectedIds.size})
              </button>
            </div>
          )}
          <div className="filter-tabs">
            <button 
              className={filter === 'ALL' ? 'active' : ''} 
              onClick={() => setFilter('ALL')}
            >
              All ({conversations.length})
            </button>
            <button 
              className={filter === 'WAITING' ? 'active' : ''} 
              onClick={() => setFilter('WAITING')}
            >
              Waiting ({conversations.filter(c => c.status === 'WAITING').length})
            </button>
            <button 
              className={filter === 'ASSIGNED' ? 'active' : ''} 
              onClick={() => setFilter('ASSIGNED')}
            >
              Active ({conversations.filter(c => c.status === 'ASSIGNED').length})
            </button>
          </div>
        </div>

        <div className="conversations-list">
          {filteredConversations.length === 0 && (
            <div className="no-conversations">
              <p>No conversations</p>
            </div>
          )}
          {filteredConversations.map((conv) => (
            <div
              key={conv.id}
              className={`conversation-item ${selectedConversation?.id === conv.id ? 'selected' : ''} ${selectMode && selectedIds.has(conv.id) ? 'checked' : ''}`}
              onClick={() => selectMode ? toggleSelectId(conv.id, { stopPropagation: () => {} }) : selectConversation(conv)}
            >
              <div className="conversation-header">
                <div className="conversation-left">
                  {selectMode && (
                    <input
                      type="checkbox"
                      className="bulk-checkbox"
                      checked={selectedIds.has(conv.id)}
                      onChange={(e) => toggleSelectId(conv.id, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <span className="conversation-user">
                    {conv.user_email || conv.collected_fields?.email || `Session ${conv.session_id.slice(0, 8)}`}
                  </span>
                </div>
                <div className="conversation-actions">
                  {getStatusBadge(conv.status)}
                  <button
                    className="btn-delete-conversation"
                    onClick={(e) => deleteConversation(conv.id, e)}
                    title="Delete conversation"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="conversation-category">
                {conv.category || 'General Inquiry'}
              </div>
              <div className="conversation-time">
                {formatTime(conv.updated_at)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="inbox-main">
        {!selectedConversation ? (
          <div className="no-selection">
            <p>Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="conversation-header-bar">
              <div className="conversation-info">
                <h3>{selectedConversation.user_email || selectedConversation.collected_fields?.email || 'Customer'}</h3>
                <span className="conversation-meta">
                  {selectedConversation.category || 'General'} • Session: {selectedConversation.session_id.slice(0, 12)}
                </span>
              </div>
              <div className="conversation-actions">
                {selectedConversation.status !== 'CLOSED' && (
                  <button className="btn-close-conv" onClick={closeConversation}>
                    Close Conversation
                  </button>
                )}
              </div>
            </div>

            <div className="conversation-messages">
              {messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.sender.toLowerCase()}`}>
                  <div className="message-sender">
                    {msg.sender === 'USER' ? 'Customer' : msg.sender === 'AGENT' ? 'AI Assistant' : 'Staff'}
                  </div>
                  <div className="message-content">{msg.content}</div>
                  <div className="message-time">{formatTime(msg.created_at)}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {selectedConversation.status !== 'CLOSED' && (
              <div className="conversation-input">
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <button onClick={sendMessage} disabled={!inputValue.trim() || isLoading}>
                  Send
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.conversationId === 'bulk' ? 'Delete Conversations' : 'Delete Conversation'}
        message={confirmDialog.conversationId === 'bulk'
          ? `Are you sure you want to delete ${selectedIds.size} conversation(s)? This action cannot be undone.`
          : 'Are you sure you want to delete this conversation? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ChatInbox;
